import { Injectable } from '@angular/core';
import { UserInfoService } from '../auth-service/user-info-service';
import { isGlobalAdministratorFromPermissions } from './permission-claims';
import { PermissionUiService } from './permission-ui.service';

@Injectable({ providedIn: 'root' })
export class PermissionAccessService {
  constructor(private readonly permissionUi: PermissionUiService) {}

  async hasAnyPermission(...permissions: Array<string | undefined | null>): Promise<boolean> {
    for (const permission of permissions) {
      if (!permission) {
        continue;
      }
      if (await UserInfoService.hasPermission(permission)) {
        return true;
      }
    }
    return false;
  }

  async canView(viewPermission: string, ...fallbackPermissions: string[]): Promise<boolean> {
    return this.hasAnyPermission(viewPermission, ...fallbackPermissions);
  }

  async canAct(actionPermission: string, ...fallbackPermissions: string[]): Promise<boolean> {
    return this.hasAnyPermission(actionPermission, ...fallbackPermissions);
  }

  async canViewClinicGroup(permission: string): Promise<boolean> {
    return this.canView(permission);
  }

  async canActClinicGroup(permission: string): Promise<boolean> {
    return this.canAct(permission);
  }

  async canViewPracticeSection(permission: string): Promise<boolean> {
    return this.canViewClinicGroup(permission);
  }

  async canActPracticeSection(permission: string): Promise<boolean> {
    return this.canActClinicGroup(permission);
  }

  async canViewUserSection(permission: string): Promise<boolean> {
    return this.canViewClinicGroup(permission);
  }

  async canActUserSection(permission: string): Promise<boolean> {
    return this.canActClinicGroup(permission);
  }

  async isGlobalAdministrator(): Promise<boolean> {
    const user = await UserInfoService.getUserInfo();
    return !!user?.isGlobalAdmin || isGlobalAdministratorFromPermissions(user?.permissions);
  }

  async enforceSectionView(
    sectionId: string,
    viewPermission: string,
    fallbackPermissions: string[] = [],
    deniedMessage?: string
  ): Promise<boolean> {
    const allowed = await this.canView(viewPermission, ...fallbackPermissions);
    if (allowed) {
      this.permissionUi.allow(sectionId);
    } else {
      this.permissionUi.deny(sectionId, deniedMessage);
    }
    return allowed;
  }
}
